(ns frontend.view.nev.company.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.roles :as roles]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.actions.common :as common-actions]
   [frontend.bootstrap.components :as rbs]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.modules.associated-entities
    :as
    associated-entities]
   [frontend.view.entity-viewers.modules.fees :as fees]
   [frontend.view.entity-viewers.modules.guests :as guests-module]
   [frontend.view.entity-viewers.modules.risk.event :as risk-event]
   [frontend.view.entity-viewers.modules.risk.view :as risk-module]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.dom-globals :as dg]
   [ui.view.nev.view :as nev-2024]))

(defn toolbar-buttons
  [entity]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)
        entity-graph-feature? @(r/track session/entity-graph?)
        new-entity-viewer-preview-2024? @(r/track session/new-company-viewer-preview-2024?)]
    (concat
     (when new-entity-viewer-preview-2024?
       (zap-toolbar/new-entity-viewer-2024-toggle-and-divider))

     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     (when entity-graph-feature?
       [{:icon :fa-diagram-project
         :toolbar-button?  false
         :dropdown-action? true
         :primary-button?  false
         :on-select (fn [_]
                      (routing/set-view! [:entity-graph :id entity-id]))
         :ord -1
         :label (loc/tr :entity-graph)}])

     [(zap-toolbar/delete-button
       entity
       [:companies-grid])]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(def fees-associated-entities-attributes
  [:common/related-entities-person
   :common/related-entities-oppo])

(def target-types
  (concat (when-not (session/is-guest?)
            [:entity.type/person
             :entity.type/company])
          (when (session/portfolio?)
            [:entity.type/holding])))

(defn module-collapsed-path
  [view-id module-id]
  [view-id :modules :module-config module-id :collapsed?])

(defn open-in-maps
  [company-id]
  (let [asset (get @session/entities company-id)]
    (dg/open-window (str "https://www.google.com/maps/place/"
                         (:asset/street-address asset)
                         ","
                         (when-let [zip (:asset/zip-code asset)]
                           (str zip "+"))
                         (:asset/city asset)))))

(defn header-top
  [{:keys [entity read-only?]}]
  (let [enriched-entity (ic/enrich-entity entity)
        company-logo (or (:company/logo entity)
                         (:company/logo enriched-entity))]
    [:div
     {:class "flex gap-2"}
     [:span.entity-logo-container.icon-container
      {:style {:padding-left "0"}}
      (if company-logo
        [rbs/Image {:src (fu/sanitize-img-url company-logo)}]
        [:i.fal.fa-building])]
     [:div.nev-name-row-container
      [nev-components/name-view
       {:entity     entity
        :read-only? read-only?}]]]))

(def default-field-defs
  [{:fields       {0 {:field :company/website}}
    :field-layout :f-4}
   {:fields       {0 {:field :asset/description}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [edit-layout? (:edit-layout? @header-state)
            entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:company :header :field-defs]
            field-defs-timeout-id :company-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            #_{:oppo/target-asset {:field/default-visibility {:editor :editor.type/link}}
               :oppo/stage        {:editor/type :editor.type/select}}
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/company
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     edit-layout?
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn company-view
  [{:keys [company-id]}]
  (let [initialized? (r/atom false)
        state-atom (r/cursor app-db/app-state [:views :nev :company])
        enrichment-state (r/cursor app-db/app-state [:views :enrichment :company])
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [company-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :company-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom company-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (when (and (session/risks-access?)
                   (f-permissions/field-read-access? :company/risk-entities
                                                     :entity.type/company
                                                     nil))
          (rf/dispatch [::risk-event/load-risks company-id])))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [company-id]}]
        (let [company @entity-atom
              enriched-entity (ic/enrich-entity company)
              enrich-datas (ic/enrichment-entities company)
              company-logo (or (:company/logo company)
                               (:company/logo enriched-entity))]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? company)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not company)
                    (:common/disabled? company))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    related-oppos (when (session/dealflow?)
                                    (->> @session/all-oppos
                                         (filter (fn [o]
                                                   ;; TODO: @pirinen - filter based on access to pipeline where deal is ?
                                                   (when-not (:common/disabled? o)
                                                     (= (:oppo/target-asset o) company-id))))))
                    related-sales-opportunities
                    (when (session/sales-pipelines?)
                      (->> @session/all-sales-opportunities
                           (remove :common/disabled?)
                           (cu/remove-by :sales-opportunity/status :sales-opportunity.status/lost)
                           (cu/remove-by :sales-opportunity/status :sales-opportunity.status/won)
                           (cu/filter-by :sales-opportunity/customer company-id)))
                    related-sale-agreements (when (session/sales-pipelines?)
                                              (->> @session/all-sale-agreements
                                                   (remove :common/disabled?)
                                                   (cu/filter-by :sale-agreement/customer
                                                                 company-id)))

                    related-holdings (when (session/portfolio?)
                                       (->> @session/all-holdings
                                            (filter
                                             (fn [h]
                                               ;; TODO: @pirinen - filter based on something else ??? -- also need access to fund if holding is linked to fund
                                               (when-not (:common/disabled? h)
                                                 (= (:holding/target-asset h) company-id))))))
                    existing-guests (->> @session/all-roles
                                         (cu/only-entity :role/role :role/guest-sell-side)
                                         (remove :common/disabled?)
                                         (cu/only-entity :role/target company-id))

                    fund-gp-in-funds (->> @session/fof-assets
                                          (remove :common/disabled?)
                                          (cu/filter-by :fof-asset/gp company-id)
                                          (sort-by entity/display-string))

                    header-fields [:asset/description :company/next-development]
                    buttons @(r/track toolbar-buttons company)
                    view-id :company
                    module-collapsed-path (r/partial module-collapsed-path view-id)]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:companies-grid]
                                               (loc/tr :back-to/companies-grid))
                     :actions         buttons
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          company
                        :enriched-entity enriched-entity
                        :logo-url        company-logo
                        :entity-atom     entity-atom
                        :url-attribute   :company/website
                        :selected-fields header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (and (session/event-pane-disabled?)
                                       (not enrich-datas))
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [modules/view
                       {:view-id             view-id
                        :ordering            @(r/track session/get-view-config [view-id :modules :ordering])
                        :save-modules-order! (r/partial events/save-modules-order!
                                                        (:entity/type company))
                        :modules
                        (concat
                         [(merge
                           {:module-id              :company-information
                            :header                 (loc/tr :nev-module/about-this-entity-type
                                                            [(string/lower-case (loc/tr :entity.type/company))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e]
                                              ;;open settings
                                              )}
                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed?             @(r/track session/get-view-config
                                                              (module-collapsed-path :company-information))
                            :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                               (:entity/type company))
                            :content                [:div
                                                     [entity-editor/view
                                                      {:state-atom    state-atom
                                                       :entity-id     company-id
                                                       :view-id       view-id
                                                       :module-id     :company-information
                                                       :remove-fields (concat [:asset/name :company/website
                                                                               :common/network-drive :common/named-network-drives]
                                                                              (field/entity-tag-fields
                                                                               tenant-schema
                                                                               (:entity/type company))
                                                                              header-fields)
                                                       :ordering      @(r/track session/get-view-config
                                                                                [view-id :modules
                                                                                 :module-config
                                                                                 :company-information
                                                                                 :ordering])}]]}
                           (when (:asset/city company)
                             {:header-tools
                              [:span.location-icon.grow.flex.justify-end
                               [button/secondary-open
                                {:tooltip (loc/tr :open-address-in-google-maps)}
                                [:i.fal.fa-map-marker-alt
                                 {:on-click (r/partial open-in-maps company-id)}]]]}))
                          (when (and (session/risks-access?)
                                     (f-permissions/field-read-access? :company/risk-entities
                                                                       :entity.type/company
                                                                       nil))
                            (risk-module/risk-module-config
                             {:module-label           (loc/tr :nev-module/company-risks)
                              :module-id              :holding-risks
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type
                                                                  :entity.type/holding))
                              :entity-id              company-id
                              :add-new-button?        false}))
                          (when (and (not (session/is-guest?))
                                     (or (session/portfolio?)
                                         (session/fundraising?)))
                            (let [module-id :company-investor
                                  order-config-path [view-id :modules :module-config module-id
                                                     :ordering]]
                              (nev-components/entity-relation-module
                               {:state-atom             state-atom
                                :module-id              module-id
                                :view-id                view-id
                                :ordering               @(r/track session/get-view-config
                                                                  order-config-path)
                                :order-config-path      order-config-path
                                :module-label           (loc/tr :nev-module/company-investor)
                                :entity                 company
                                :relationship-attribute :investor/legal-entity
                                :remove-fields          [:investor/legal-entity]
                                :reverse-relationship?  true})))]
                         (when-not (session/is-guest?)
                           (associated-entities/modules
                            {:entity-id              company-id
                             :save-module-collapsed! events/save-module-collapsed!
                             :module-collapsed-path  module-collapsed-path}))
                         (when (session/fee-tracking?)
                           (fees/modules {:entity-id              company-id
                                          :save-module-collapsed! events/save-module-collapsed!
                                          :module-collapsed-path  module-collapsed-path
                                          :fees-associated-entities-attributes
                                          fees-associated-entities-attributes}))
                         [(when (and (not (session/is-guest?))
                                     (session/dealflow?))
                            (nev-components/association-module-config
                             {:module-label           (loc/tr :nev-module/company-target-asset-in-oppos)
                              :module-id              :target-asset-in-oppos
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :target-entity-type     :entity.type/oppo
                              :associated-entities    related-oppos
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type company))
                              :new-entity-start-value {:oppo/target-asset (:db/id company)
                                                       :oppo/name         (:asset/name company)}
                              :add-new-button?        true
                              :add-new-content
                              (when (session/deal-creation-flow?)
                                [nev-components/opportunity-creator-dropdown
                                 {:entity-type :entity.type/oppo
                                  :initial-target company}])}))
                          (when (and (not (session/is-guest?))
                                     (session/sales-pipelines?)
                                     (seq related-sales-opportunities))
                            (nev-components/association-module-config
                             {:module-label           (loc/tr :nev-module/company-customer-in-sales-oppos)
                              :module-id              :customer-in-sales-oppos
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :target-entity-type     :entity.type/sales-opportunity
                              :associated-entities    related-sales-opportunities
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type company))
                              :add-new-button?        false}))
                          (when (and (not (session/is-guest?))
                                     (session/sales-pipelines?)
                                     (seq related-sale-agreements))
                            (nev-components/association-module-config
                             {:module-label           (loc/tr :nev-module/company-customer-in-sale-agreements)
                              :module-id              :customer-in-sale-agreements
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :target-entity-type     :entity.type/sale-agreement
                              :associated-entities    related-sale-agreements
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type company))
                              :add-new-button?        false}))

                          (when (session/portfolio?)
                            (nev-components/association-module-config
                             {:module-label           (loc/tr
                                                       :nev-module/company-target-asset-in-holdings)
                              :module-id              :target-asset-in-holdings
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :target-entity-type     :entity.type/holding
                              :associated-entities    related-holdings
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type company))}))

                          (when (and (not (session/is-guest?))
                                     (session/has-feature-access? :feature/asset-types-fof))
                            (nev-components/association-module-config
                             {:module-label           (loc/tr :nev-module/company-gp-funds)
                              :module-id              :fund-gp-in-funds
                              :view-id                view-id
                              :nev-entity-type        :entity.type/company
                              :target-entity-type     :entity.type/fof-asset
                              :associated-entities    fund-gp-in-funds
                              :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                 (:entity/type company))
                              :new-entity-start-value {:fof-asset/gp company-id}
                              :add-new-button?        true}))

                          (when (and (not (session/is-guest?))
                                     (plan/available :feature/sell-side-guest
                                                     @session/tenant
                                                     @session/role)
                                     (or (seq existing-guests)
                                         (not (session/is-read-only-user?))))
                            {:module-id              :guest-accounts
                             :view-id                view-id
                             :header                 (loc/tr :nev-module/guest-accounts)
                             :collapsed?             @(r/track session/get-view-config
                                                               (module-collapsed-path :guest-accounts))
                             :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                                :entity.type/company)
                             :start-collapsed?       (empty? existing-guests)
                             :content                [:div
                                                      [guests-module/view
                                                       {:entity-id          company-id
                                                        :fields             [{:attribute
                                                                              :role/guest-sell-side
                                                                              :label
                                                                              (loc/tr :sell-side-guests)}]
                                                        :show-notification! (r/partial
                                                                             events/show-notification!
                                                                             notification-atom)
                                                        :show-error!        (r/partial events/show-error!
                                                                                       error-atom)
                                                        :guest-roles        existing-guests}]]})

                          (when (and (not (session/is-mobile?))
                                     (f-permissions/field-read-access? :common/attachments
                                                                       :entity.type/company
                                                                       nil))
                            (nev-components/attachments-module view-id
                                                               :company-attachments
                                                               company))])}]]

                     (when-not (session/is-guest?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        (when enrich-datas
                          [nev-components/enrichment-module
                           {:module-id              :company-enrichment-data
                            :collapsed?             @(r/track session/get-view-config
                                                              (module-collapsed-path :company-enrichment-data))
                            :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                               :entity.type/company)
                            :view-id                view-id
                            :state                  enrichment-state
                            :entities               enrich-datas}])
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [company-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defn new-company-viewer-2024
  [{:keys [company-id]}]
  (let [state-path [:nev-2024 :company company-id]]
    [:div
     [nev-2024/view-with-data-fetching
      {:state-path  state-path
       :entity-id   company-id
       :entity-type :entity.type/company}]]))

(defn company-view-wrapper
  [{:keys [company-id]}]
  (let [classic-entity-viewer-enabled? @(r/track session/classic-entity-viewer-enabled?)]
    (if classic-entity-viewer-enabled?
      [company-view {:company-id company-id}]
      [new-company-viewer-2024 {:company-id company-id}])))

(defmethod routing/render-view :entity.type/company
  [{:keys [route-params _view-params]}]
  (let [company-id (-> route-params
                       :id
                       js/parseInt)]
    [default-page-layout/view
     ;;the key change forces re-rendering of the component (and its child components)
     ^{:key (str "company-viewer-id-" company-id)}
     [company-view-wrapper {:company-id company-id}]]))

(defmethod routing/render-view :company-guest
  [_]
  ;; TODO can also pass roles here to enable buy-side-guests later
  (if-not (roles/is-guest-role? @session/role)
    [not-found/not-found-view]
    [default-page-layout/view
     ^{:key (str "company-viewer-id-" @entity-utils/force-remount-atom)}
     [company-view
      {:company-id @session/target-id}]]))
